import { useEffect, useState, useCallback, useRef } from 'react'
import { useAttachmentStore } from '../../store'
import { Button } from '@highlight/ui'
import { getMessageListElement } from './message-list/scroll-atoms'
import { FLOATING_FILL_CLASS } from '@renderer/components/floating-surface'
import { twMerge } from 'tailwind-merge'

export default function QuoteReferenceSelector({ messageId }: { messageId: string }) {
  const { addQuoteReferenceAttachment } = useAttachmentStore()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const messageElementRef = useRef<HTMLElement | null>(null)

  const [buttonPos, setButtonPos] = useState<{
    top: number
    left: number
  } | null>(null)
  const [selectedText, setSelectedText] = useState('')

  const handleQuoteClick = useCallback(() => {
    if (selectedText) {
      addQuoteReferenceAttachment(selectedText)
      window.ampltd.trackEvent('HLC Message Quote Quoted', {
        messageId,
      })
    }
    window.getSelection()?.removeAllRanges()
    setButtonPos(null)
    setSelectedText('')
  }, [selectedText, addQuoteReferenceAttachment, messageId])

  const updateSelectionPosition = useCallback(() => {
    const sel = window.getSelection()
    if (!sel || sel.isCollapsed) {
      setButtonPos(null)
      setSelectedText('')
      return
    }

    // Use the cached message element reference
    const msgEl = messageElementRef.current
    if (!msgEl || sel.rangeCount === 0) {
      setButtonPos(null)
      setSelectedText('')
      return
    }

    const range = sel.getRangeAt(0)
    if (!msgEl.contains(range.commonAncestorContainer)) {
      // Selection is not in our message
      setButtonPos(null)
      setSelectedText('')
      return
    }

    // Selection IS in our message - process it
    const text = sel.toString().trim()
    if (!text) {
      setButtonPos(null)
      setSelectedText('')
      return
    }

    const selRect = range.getBoundingClientRect()
    let startRect: DOMRect | null = null
    try {
      const c = range.startContainer
      const off = range.startOffset
      const maxOff = c.nodeType === Node.TEXT_NODE ? (c as Text).length : c.childNodes.length
      const endOff = Math.min(off + 1, maxOff)
      const r = document.createRange()
      r.setStart(c, off)
      r.setEnd(c, endOff)
      startRect = r.getBoundingClientRect()
    } catch {
      startRect = null
    }
    if (!startRect) {
      setButtonPos(null)
      setSelectedText('')
      return
    }

    const list = getMessageListElement()
    if (list) {
      const lr = list.getBoundingClientRect()
      if (selRect.top < lr.top || selRect.bottom > lr.bottom || selRect.left < lr.left || selRect.right > lr.right) {
        // Selection is outside the visible list area
        setButtonPos(null)
        setSelectedText('')
        return
      }
    }

    const msgRect = msgEl.getBoundingClientRect()
    const verticalOffset = 8
    const btnHeight = 38
    const nearTop = Math.abs(selRect.top - msgRect.top) < 10
    let topRel = nearTop ? selRect.bottom - msgRect.top + verticalOffset : selRect.top - msgRect.top - btnHeight
    let leftRel = Math.max(4, Math.min(startRect.left - msgRect.left, msgRect.width - 100))

    if (topRel < -40 || topRel > msgRect.height - btnHeight) {
      // Button position would be outside valid area
      setButtonPos(null)
      setSelectedText('')
      return
    }

    // This is our selection - set the state
    setButtonPos({
      top: topRel,
      left: leftRel,
    })
    setSelectedText(text)
  }, [messageId])

  // Set up message element reference
  useEffect(() => {
    messageElementRef.current = document.getElementById(`message-${messageId}`)
  }, [messageId])

  useEffect(() => {
    const messageElement = messageElementRef.current
    if (!messageElement) return

    const onMouseUp = (e: MouseEvent) => {
      // Small delay to ensure selection is finalized
      setTimeout(() => updateSelectionPosition(), 10)
    }

    const onMouseDown = (e: MouseEvent) => {
      if (buttonRef.current?.contains(e.target as Node)) return

      // Only clear if click was outside our message or on our button
      if (!messageElement.contains(e.target as Node)) {
        setButtonPos(null)
        setSelectedText('')
      }
    }

    // Listen only to events on this specific message
    messageElement.addEventListener('mouseup', onMouseUp)
    document.addEventListener('mousedown', onMouseDown) // Keep global for outside clicks

    return () => {
      messageElement.removeEventListener('mouseup', onMouseUp)
      document.removeEventListener('mousedown', onMouseDown)
    }
  }, [messageId, updateSelectionPosition])

  // Global selection change listener (but with early exit)
  useEffect(() => {
    const onSelectionChange = () => {
      const sel = window.getSelection()
      if (!sel || sel.isCollapsed) {
        // Clear state if we have any
        setButtonPos((prev) => (prev ? null : prev))
        setSelectedText((prev) => (prev ? '' : prev))
        return
      }

      // Quick check: if selection is not in our message, don't process
      const messageElement = messageElementRef.current
      if (!messageElement || sel.rangeCount === 0) return

      const range = sel.getRangeAt(0)
      if (!messageElement.contains(range.commonAncestorContainer)) {
        // Selection moved outside our message
        setButtonPos((prev) => (prev ? null : prev))
        setSelectedText((prev) => (prev ? '' : prev))
      }
    }

    document.addEventListener('selectionchange', onSelectionChange)
    return () => {
      document.removeEventListener('selectionchange', onSelectionChange)
    }
  }, [messageId])

  if (!buttonPos) return null

  return (
    <Button
      ref={buttonRef}
      variant="outline"
      size="sm"
      shape="rounded"
      // Floats over the message text, so the fill has to be opaque or the text reads through it.
      className={twMerge('absolute z-[2] shadow-sm', FLOATING_FILL_CLASS)}
      style={{
        top: buttonPos.top,
        left: buttonPos.left,
      }}
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation()
        handleQuoteClick()
      }}
    >
      Ask follow-up
    </Button>
  )
}
